import {
  processZipArchive,
  ArchiveFile,
  ProcessedArchive,
} from "./ZipProcessor";

export interface DownloadProgress {
  phase: "downloading" | "extracting" | "processing";
  bytesDownloaded?: number;
  totalBytes?: number;
  filesProcessed?: number;
  totalFiles?: number;
}

export const downloadGitHubArchive = async (
  owner: string,
  repo: string,
  branch: string,
  onProgress?: (progress: DownloadProgress) => void
): Promise<{ repoData: Record<string, ArchiveFile>; totalFiles: number }> => {
  console.debug(
    `[GitHubArchiveDownloader] Starting download of ${owner}/${repo}@${branch} via server`
  );

  // Check if user is authenticated (has access token)
  const accessToken = localStorage.getItem("accessToken");
  const isAuthenticated =
    accessToken && accessToken !== "null" && accessToken !== "undefined";

  onProgress?.({ phase: "downloading" });

  try {
    // Use CodeCanvas server as proxy to avoid CORS issues
    const serverEndpoint =
      process.env.REACT_APP_BACKEND_URL || "http://localhost:8080";
    const requestBody = {
      owner,
      repo,
      branch,
      isLoggedIn: isAuthenticated,
    };

    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    };

    // Add authorization header if user is authenticated
    if (isAuthenticated) {
      headers["Authorization"] = accessToken;
    }

    console.debug(
      `[GitHubArchiveDownloader] Making server request to ${serverEndpoint}/downloadRepositoryArchive`
    );

    const response = await fetch(
      `${serverEndpoint}/downloadRepositoryArchive`,
      {
        method: "POST",
        headers,
        body: JSON.stringify(requestBody),
        credentials: "include", // Include cookies for session management
      }
    );

    if (!response.ok) {
      // Try to get error details from response
      let errorMessage = `Server request failed: ${response.status} ${response.statusText}`;
      try {
        const errorData = await response.json();
        if (errorData.error) {
          errorMessage = errorData.error;
        }
      } catch (e) {
        // If parsing error response fails, use default message
      }
      throw new Error(errorMessage);
    }

    const result = await response.json();

    if (!result.success || !result.downloadUrl) {
      throw new Error("Invalid response from server: missing download URL");
    }

    console.debug(
      `[GitHubArchiveDownloader] Received download URL for archive (${(
        result.archiveSize /
        1024 /
        1024
      ).toFixed(2)}MB), downloading from S3`
    );

    // Download the archive directly from S3 using the presigned URL
    const archiveResponse = await fetch(result.downloadUrl);

    if (!archiveResponse.ok) {
      throw new Error(
        `Failed to download archive from S3: ${archiveResponse.status} ${archiveResponse.statusText}`
      );
    }

    // Get the archive as ArrayBuffer
    const archiveBuffer = await archiveResponse.arrayBuffer();

    console.debug(
      `[GitHubArchiveDownloader] Successfully downloaded archive from S3 (${(
        archiveBuffer.byteLength /
        1024 /
        1024
      ).toFixed(2)}MB)`
    );

    // Convert ArrayBuffer to base64 for the zip processor
    onProgress?.({ phase: "extracting" });

    console.debug(
      `[GitHubArchiveDownloader] Converting archive to base64 for zip processing`
    );

    // Convert ArrayBuffer to base64
    const uint8Array = new Uint8Array(archiveBuffer);
    let binaryString = "";
    const chunkSize = 8192; // Process in chunks to avoid stack overflow

    for (let i = 0; i < uint8Array.length; i += chunkSize) {
      const chunk = uint8Array.slice(i, i + chunkSize);
      binaryString += String.fromCharCode.apply(null, Array.from(chunk));
    }

    const archiveData = btoa(binaryString);

    console.debug(
      `[GitHubArchiveDownloader] Starting client-side extraction of zip archive`
    );

    const processedArchive = await processZipArchive(
      archiveData,
      (current, total) => {
        onProgress?.({
          phase: "processing",
          filesProcessed: current,
          totalFiles: total,
        });
      }
    );

    console.debug(
      `[GitHubArchiveDownloader] Successfully processed ${processedArchive.totalFiles} files on client`
    );

    return processedArchive;
  } catch (error) {
    console.error(
      `[GitHubArchiveDownloader] Download/extraction failed:`,
      error
    );

    // For server errors, provide helpful guidance
    if (error instanceof Error) {
      // Handle zip processing errors specifically
      if (error.message.includes("Failed to process zip archive")) {
        throw new Error(
          `Failed to extract repository contents: ${error.message}\n\n` +
            `This could mean:\n` +
            `• The downloaded file is corrupted\n` +
            `• The file is too large to process in your browser\n\n` +
            `💡 Try using Local mode instead for large repositories.`
        );
      }

      if (error.message.includes("Authentication failed")) {
        throw new Error(
          `Authentication failed for repository ${owner}/${repo}.\n\n` +
            `This could mean:\n` +
            `• Your access token has expired\n` +
            `• You don't have access to this private repository\n` +
            `• The repository requires different permissions\n\n` +
            `💡 Try logging out and logging back in, or use Local mode instead.`
        );
      }

      if (error.message.includes("Repository or branch not found")) {
        throw new Error(
          `Repository or branch not found: ${owner}/${repo}@${branch}\n\n` +
            `Please check that:\n` +
            `• The repository exists and you have access to it\n` +
            `• The branch name is correct\n` +
            `• For private repositories, make sure you're logged in\n\n` +
            `💡 For private repositories, you may need to use Local mode.`
        );
      }

      if (error.message.includes("Access forbidden")) {
        throw new Error(
          `Access forbidden to repository ${owner}/${repo}.\n\n` +
            `This usually means:\n` +
            `• The repository is private and you don't have access\n` +
            `• You've hit GitHub's rate limit\n\n` +
            `💡 For private repositories, please use Local mode instead.`
        );
      }

      // Handle network/server connection errors
      if (
        error.message.includes("Failed to fetch") ||
        error.message.includes("Server request failed")
      ) {
        throw new Error(
          `Unable to connect to CodeCanvas server.\n\n` +
            `This could mean:\n` +
            `• The server is temporarily unavailable\n` +
            `• Network connectivity issues\n\n` +
            `💡 Please try again later or use Local mode instead.`
        );
      }
    }

    // Re-throw the original error if we can't categorize it
    throw error;
  }
};
